import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';
import type { IaHandoffBin } from '../../../types';
import type { FunilIaStageSlug } from '../funilIaStage';
import type { PhysicsBall, BallCommand, BallEvent, StepContext, TrailParticle } from '../engine/types';
import { stepBall } from '../engine/stepBall';
import {
  BALL_RADIUS,
  DESCENT_DURATION_MS,
  IMPULSE_FORCE,
  HANDOFF_BIN_SUCK_TRAIL,
  SUCK_MOUTH_ARRIVAL_PX,
} from '../engine/constants';
import type { PhysicsTweaks } from '../physicsTweaks';
import type { FunilCampaignSwatch } from '../funilCampaignPalette';
import { syncPhysicsBallDom } from '../physicsBallDomSync';

interface UsePhysicsEngineArgs {
  laneRefs: Record<FunilIaStageSlug, React.RefObject<HTMLElement | null>>;
  humanRefs: Record<FunilIaStageSlug, React.RefObject<HTMLElement | null>>;
  onHandoffComplete: (leadId: string) => void;
  /** Ajustes em tempo real (painel Tweaks); lido a cada frame sem re-render do pai. */
  tweaksRef: MutableRefObject<PhysicsTweaks>;
  /** Resolução de cores por campanha (para CSS vars na sincronização DOM). */
  swatchResolverRef: MutableRefObject<(campaignId: string) => FunilCampaignSwatch>;
  /**
   * Quando `false` (p.ex. Funil IA physics alinhado ao protótipo), não aplica
   * `funil-ia-ball--campaign` — a bolicha usa o gradiente por `lane` (borda esquerda).
   * Lido a cada *frame*; omição => tratado como `true` (comportamento compatível).
   */
  useCampaignBallSwatchRef?: MutableRefObject<boolean>;
  /** F4.5: coluna de bins de handoff; alvo de sucção partilhado. */
  handoffBinsUi?: boolean;
  binRefs?: Record<IaHandoffBin, React.RefObject<HTMLElement | null>>;
}

export interface UsePhysicsEngineReturn {
  balls: Map<string, PhysicsBall>;
  trails: Record<FunilIaStageSlug, TrailParticle[]>;
  paused: boolean;
  setPaused: (v: boolean) => void;
  /** Queue a BallCommand for the next frame */
  enqueueCommand: (cmd: BallCommand) => void;
  /** Regista o nó DOM da bolinha para atualização por rAF sem re-render React. */
  registerBallElement: (leadId: string, el: HTMLElement | null) => void;
}

function rand(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

function measureLane(ref: React.RefObject<HTMLElement | null>): { width: number; height: number } | null {
  const el = ref.current;
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  return { width: rect.width, height: rect.height };
}

function humanCenterInRoad(
  laneRef: React.RefObject<HTMLElement | null>,
  humanRef: React.RefObject<HTMLElement | null>,
): { x: number; y: number } | null {
  const road = laneRef.current;
  const human = humanRef.current;
  if (!road || !human) return null;
  const roadRect = road.getBoundingClientRect();
  const target = human.querySelector('.funil-ia-human-target') as HTMLElement | null;
  if (!target) return null;
  const h = target.getBoundingClientRect();
  return {
    x: (h.left + h.right) / 2 - roadRect.left,
    y: (h.top + h.bottom) / 2 - roadRect.top,
  };
}

/** Posição do alvo de handoff (cartão *human* na lane, ou cartão de bin na coluna F4.5) em coords locais da *road*. */
function handoffTargetInRoad(
  laneRef: React.RefObject<HTMLElement | null>,
  useBins: boolean,
  humanRef: React.RefObject<HTMLElement | null>,
  bin: IaHandoffBin,
  binRefs: Record<IaHandoffBin, React.RefObject<HTMLElement | null>> | undefined,
): { x: number; y: number } | null {
  const road = laneRef.current;
  if (!road) return null;
  const roadRect = road.getBoundingClientRect();
  if (useBins && binRefs) {
    const wrap = binRefs[bin]?.current;
    const target = (wrap?.querySelector('.funil-ia-handoff-bin-target') as HTMLElement) || wrap;
    if (!target) return null;
    const h = target.getBoundingClientRect();
    return {
      x: (h.left + h.right) / 2 - roadRect.left,
      y: (h.top + h.bottom) / 2 - roadRect.top,
    };
  }
  return humanCenterInRoad(laneRef, humanRef);
}

/**
 * Protótipo GHI-FunilAI: 1) boca (semi-cápsula) entre lane e cartão, 2) corpo do bin.
 */
function resolveSuckTargetInRoad(
  ball: PhysicsBall,
  laneRef: React.RefObject<HTMLElement | null>,
  useBins: boolean,
  humanRef: React.RefObject<HTMLElement | null>,
  binRefs: Record<IaHandoffBin, React.RefObject<HTMLElement | null>> | undefined,
): { x: number; y: number } | null {
  if (!useBins || !binRefs) {
    return handoffTargetInRoad(laneRef, false, humanRef, ball.suckTargetBin ?? 'human', undefined);
  }
  const bin = ball.suckTargetBin ?? 'human';
  const wrap = binRefs[bin]?.current;
  if (!wrap) return null;
  const road = laneRef.current;
  if (!road) return null;
  const roadRect = road.getBoundingClientRect();
  const mouthEl = wrap.querySelector('.funil-ia-handoff-bin-mouth') as HTMLElement | null;
  const bodyEl = wrap.querySelector('.funil-ia-handoff-bin-body') as HTMLElement | null;
  const settleFrom = (bodyEl ||
    (wrap.querySelector('.funil-ia-handoff-bin-target') as HTMLElement) ||
    wrap) as HTMLElement;
  const srect = settleFrom.getBoundingClientRect();
  const settle = {
    x: (srect.left + srect.right) / 2 - roadRect.left,
    y: (srect.top + srect.bottom) / 2 - roadRect.top,
  };
  if (!mouthEl) {
    return settle;
  }
  const mrect = mouthEl.getBoundingClientRect();
  const mouth = {
    x: (mrect.left + mrect.right) / 2 - roadRect.left,
    y: (mrect.top + mrect.bottom) / 2 - roadRect.top,
  };
  if (ball.suckSubPhase === 'mouth') {
    if (Math.hypot(ball.x - mouth.x, ball.y - mouth.y) < SUCK_MOUTH_ARRIVAL_PX) {
      ball.suckSubPhase = 'settle';
    }
  }
  if (ball.suckSubPhase === 'mouth') {
    return mouth;
  }
  return settle;
}

export function usePhysicsEngine({
  laneRefs,
  humanRefs,
  onHandoffComplete,
  tweaksRef,
  swatchResolverRef,
  useCampaignBallSwatchRef,
  handoffBinsUi = false,
  binRefs: binRefsArg,
}: UsePhysicsEngineArgs): UsePhysicsEngineReturn {
  /** Não fornecida => mantém o swatch de campanha (compat). */
  const useCampaignByDefaultRef = useRef(true);
  const useCampaignRef = useCampaignBallSwatchRef ?? useCampaignByDefaultRef;
  const [, forceRender] = useState(0);
  const [paused, setPaused] = useState(false);

  const ballsRef = useRef<Map<string, PhysicsBall>>(new Map());
  const ballDomRefsRef = useRef<Map<string, HTMLElement>>(new Map());
  const trailsRef = useRef<Record<FunilIaStageSlug, TrailParticle[]>>({
    inicio: [],
    em_negociacao_ia: [],
    proposta_ia: [],
  });
  const commandQueueRef = useRef<BallCommand[]>([]);
  const rafRef = useRef(0);
  const lastTsRef = useRef(performance.now());
  const pausedRef = useRef(paused);
  const onHandoffCompleteRef = useRef(onHandoffComplete);
  onHandoffCompleteRef.current = onHandoffComplete;
  const handoffBinsUiRef = useRef(handoffBinsUi);
  handoffBinsUiRef.current = handoffBinsUi;
  const binRefsRef = useRef(binRefsArg);
  binRefsRef.current = binRefsArg;
  const reducedMotionRef = useRef(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const set = () => {
      reducedMotionRef.current = mq.matches;
    };
    set();
    mq.addEventListener('change', set);
    return () => mq.removeEventListener('change', set);
  }, []);

  useEffect(() => {
    pausedRef.current = paused;
  }, [paused]);

  const enqueueCommand = useCallback((cmd: BallCommand) => {
    commandQueueRef.current.push(cmd);
  }, []);

  const registerBallElement = useCallback((leadId: string, el: HTMLElement | null) => {
    if (el) {
      ballDomRefsRef.current.set(leadId, el);
    } else {
      ballDomRefsRef.current.delete(leadId);
    }
  }, []);

  // Pending creates with stagger delays
  const pendingCreatesRef = useRef<Array<{ cmd: BallCommand; readyAt: number }>>([]);

  function processCommands(ts: number): { needsReact: boolean } {
    // Process queued commands
    const queue = commandQueueRef.current.splice(0);
    let needsReact = false;
    for (const cmd of queue) {
      switch (cmd.type) {
        case 'create': {
          if (cmd.staggerDelay > 0) {
            pendingCreatesRef.current.push({ cmd, readyAt: ts + cmd.staggerDelay });
          } else {
            needsReact = true;
            createBall(cmd, ts);
          }
          break;
        }
        case 'remove': {
          needsReact = true;
          const ball = ballsRef.current.get(cmd.leadId);
          if (!ball) break;
          if (cmd.animation === 'suck') {
            ball.visualState = 'sucked';
            ball.suckTargetBin = cmd.suckTargetBin ?? 'human';
            if (handoffBinsUiRef.current && binRefsRef.current) {
              const w = binRefsRef.current[ball.suckTargetBin]?.current;
              ball.suckSubPhase = w?.querySelector('.funil-ia-handoff-bin-mouth') ? 'mouth' : undefined;
            } else {
              ball.suckSubPhase = undefined;
            }
            ball.transitionMeta = {
              type: 'suck',
              startTime: ts,
              duration: 0,
              fromPos: { x: ball.x, y: ball.y },
            };
          } else {
            ball.visualState = 'exiting';
          }
          break;
        }
        case 'transition': {
          needsReact = true;
          const ball = ballsRef.current.get(cmd.leadId);
          if (!ball) break;
          const fromLane = measureLane(laneRefs[ball.lane]);
          ball.visualState = 'descending';
          ball.transitionMeta = {
            type: 'descend',
            startTime: ts,
            duration: DESCENT_DURATION_MS,
            fromPos: { x: ball.x, y: ball.y },
            fromLaneWidth: fromLane?.width,
            targetLane: cmd.toLane,
          };
          break;
        }
        case 'updateVisual': {
          const ball = ballsRef.current.get(cmd.leadId);
          if (!ball) break;
          if (ball.visualState === 'idle' || ball.visualState === 'thinking') {
            ball.visualState = cmd.thinking ? 'thinking' : 'idle';
            ball.pendingThinking = false;
          } else if (ball.visualState === 'entering') {
            ball.pendingThinking = Boolean(cmd.thinking);
          }
          break;
        }
        case 'impulse': {
          const ball = ballsRef.current.get(cmd.leadId);
          if (!ball) break;
          if (ball.visualState === 'idle' || ball.visualState === 'thinking') {
            ball.impulseVx += IMPULSE_FORCE;
          }
          break;
        }
      }
    }

    // Process pending staggered creates
    const pending = pendingCreatesRef.current;
    for (let i = pending.length - 1; i >= 0; i--) {
      if (ts >= pending[i].readyAt) {
        needsReact = true;
        createBall(pending[i].cmd as BallCommand & { type: 'create' }, ts);
        pending.splice(i, 1);
      }
    }
    return { needsReact };
  }

  function createBall(cmd: BallCommand & { type: 'create' }, ts: number) {
    if (ballsRef.current.has(cmd.leadId)) return; // Already exists

    const laneSize = measureLane(laneRefs[cmd.lane]);
    const metaWidth = 120; // Approximate meta rect width
    const startX = metaWidth + rand(10, 40);
    const pad = BALL_RADIUS + 4;
    const startY = laneSize
      ? rand(pad, Math.max(pad + 2, laneSize.height - pad))
      : 80;
    const targetX = startX + rand(30, 80);

    const ball: PhysicsBall = {
      leadId: cmd.leadId,
      lane: cmd.lane,
      x: startX,
      y: startY,
      vx: 0.5, // Initial rightward impulse
      vy: 0,
      radius: BALL_RADIUS,
      wobble: Math.random() * Math.PI * 2,
      scale: 1,
      opacity: 1,
      visualState: 'entering',
      impulseVx: 0,
      pendingThinking: Boolean(cmd.startThinking),
      transitionMeta: {
        type: 'entrance',
        startTime: ts,
        duration: 400,
        fromPos: { x: startX, y: startY },
        toPos: { x: targetX, y: startY },
      },
      initial: cmd.initial,
      leadName: cmd.leadName,
      campaignId: cmd.campaignId,
    };

    ballsRef.current.set(cmd.leadId, ball);
  }

  function processEvents(events: BallEvent[], ts: number): boolean {
    let needsReact = false;
    for (const evt of events) {
      switch (evt.type) {
        case 'spawn_trail': {
          trailsRef.current[evt.lane]?.push({
            x: evt.x,
            y: evt.y,
            born: ts,
            life: evt.life,
            color: evt.color,
          });
          break;
        }
        case 'descend_complete': {
          needsReact = true;
          const ball = ballsRef.current.get(evt.leadId);
          if (!ball) break;
          const targetSize = measureLane(laneRefs[evt.targetLane]);
          const wDest = targetSize?.width ?? 400;
          const meta = ball.transitionMeta;
          const wSrc =
            meta && meta.type === 'descend' && typeof meta.fromLaneWidth === 'number' && meta.fromLaneWidth > 0
              ? meta.fromLaneWidth
              : wDest;
          const xEnd = ball.x;
          const r = ball.radius;
          const minX = r + 2;
          const maxX = Math.max(minX, wDest - r - 2);
          const ratio = wSrc > 0 ? wDest / wSrc : 1;
          const mapped = xEnd * ratio;
          const xMapped = Math.max(minX, Math.min(maxX, mapped));

          ball.lane = evt.targetLane;
          ball.visualState = 'idle';
          ball.transitionMeta = undefined;
          ball.x = xMapped;
          const py = r + 4;
          ball.y = rand(py, targetSize ? Math.max(py + 2, targetSize.height - py) : 150 - py);
          ball.vx = 0.3;
          ball.vy = 0;
          ball.scale = 1;
          ball.opacity = 1;
          break;
        }
        case 'consumed': {
          needsReact = true;
          const ball = ballsRef.current.get(evt.leadId);
          const targetBin = ball?.suckTargetBin ?? 'human';
          ballsRef.current.delete(evt.leadId);
          ballDomRefsRef.current.delete(evt.leadId);
          onHandoffCompleteRef.current(evt.leadId);

          if (handoffBinsUiRef.current && binRefsRef.current) {
            const wrap = binRefsRef.current[targetBin]?.current;
            const pulseEl = (wrap?.querySelector('.funil-ia-handoff-bin-target') as HTMLElement) || wrap;
            if (pulseEl) {
              pulseEl.classList.remove('funil-ia-pulse');
              void pulseEl.offsetWidth;
              pulseEl.classList.add('funil-ia-pulse');
            }
          } else {
            const humanEl = humanRefs[evt.lane]?.current?.querySelector('.funil-ia-human-target');
            if (humanEl) {
              humanEl.classList.remove('funil-ia-pulse');
              void (humanEl as HTMLElement).offsetWidth; // Force reflow
              humanEl.classList.add('funil-ia-pulse');
            }
          }
          break;
        }
        case 'exit_complete': {
          needsReact = true;
          ballsRef.current.delete(evt.leadId);
          ballDomRefsRef.current.delete(evt.leadId);
          break;
        }
      }
    }
    return needsReact;
  }

  // Main rAF loop
  useEffect(() => {
    const tick = () => {
      const ts = performance.now();
      const dt = Math.min(48, ts - lastTsRef.current);
      lastTsRef.current = ts;

      // Always process commands (even when paused, for UI responsiveness)
      const { needsReact: cmdNeedsReact } = processCommands(ts);
      const tw = tweaksRef.current;
      const lanes: FunilIaStageSlug[] = ['inicio', 'em_negociacao_ia', 'proposta_ia'];

      for (const lane of lanes) {
        const trails = trailsRef.current[lane];
        for (let i = trails.length - 1; i >= 0; i--) {
          if (ts - trails[i].born > trails[i].life) trails.splice(i, 1);
        }
      }

      const hasBalls = ballsRef.current.size > 0;

      if (!pausedRef.current && hasBalls) {
        const allEvents: BallEvent[] = [];

        for (const lane of lanes) {
          const laneSize = measureLane(laneRefs[lane]);
          if (!laneSize) continue;
          const useBins = handoffBinsUiRef.current;
          const bins = binRefsRef.current;

          ballsRef.current.forEach((ball) => {
            if (ball.lane !== lane) return;
            const humanTarget =
              ball.visualState === 'sucked'
                ? resolveSuckTargetInRoad(ball, laneRefs[lane], useBins, humanRefs[lane], bins)
                : null;
            const suckTrailColor =
              ball.visualState === 'sucked'
                ? HANDOFF_BIN_SUCK_TRAIL[ball.suckTargetBin ?? 'human']
                : undefined;
            const ctx: StepContext = {
              laneWidth: laneSize.width,
              laneHeight: laneSize.height,
              humanTarget,
              ts,
              dt,
              speed: (dt / 16.67) * tw.speed,
              showTrails: tw.showTrails,
              gravityStrength: tw.gravityStrength,
              reducedMotion: reducedMotionRef.current,
              suckTrailColor,
            };
            const events = stepBall(ball, ctx);
            allEvents.push(...events);
          });
        }

        const structureDirty = processEvents(allEvents, ts);

        ballsRef.current.forEach((ball, leadId) => {
          const el = ballDomRefsRef.current.get(leadId);
          if (!el) return;
          const sw = useCampaignRef.current
            ? swatchResolverRef.current(ball.campaignId)
            : undefined;
          syncPhysicsBallDom(el, ball, sw);
        });

        if (cmdNeedsReact || structureDirty) {
          forceRender((n) => (n + 1) % 1_000_000);
        }
      } else if (pausedRef.current && hasBalls) {
        ballsRef.current.forEach((ball, leadId) => {
          const el = ballDomRefsRef.current.get(leadId);
          if (!el) return;
          const sw = useCampaignRef.current
            ? swatchResolverRef.current(ball.campaignId)
            : undefined;
          syncPhysicsBallDom(el, ball, sw);
        });
        if (cmdNeedsReact) {
          forceRender((n) => (n + 1) % 1_000_000);
        }
      } else if (cmdNeedsReact) {
        forceRender((n) => (n + 1) % 1_000_000);
      }

      for (const lane of lanes) {
        const road = laneRefs[lane]?.current;
        const laneEl = road?.closest('.funil-ia-lane');
        if (!laneEl) continue;
        let pulling = false;
        ballsRef.current.forEach((b) => {
          if (b.lane === lane && b.visualState === 'sucked') pulling = true;
        });
        laneEl.classList.toggle('funil-ia-lane--pulling', pulling);
      }
    };

    const loop = () => {
      tick();
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);

    // Watchdog: if rAF hasn't fired recently (throttled tab), run at ~30fps
    const iv = setInterval(() => {
      if (performance.now() - lastTsRef.current > 200) tick();
    }, 33);

    return () => {
      cancelAnimationFrame(rafRef.current);
      clearInterval(iv);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    balls: ballsRef.current,
    trails: trailsRef.current,
    paused,
    setPaused,
    enqueueCommand,
    registerBallElement,
  };
}
